import type { ChannelSlice } from '../ChannelSlice';
import { NEW_TABLE_RESET } from '../TableModel';
import type { WidgetHeaderElement } from '../WidgetModel';
import { COMMON_HEADER_ELEMENTS, STANDALONE_COMMON_HEADER_ELEMENTS } from '../WidgetModel/impl';
import type { WidgetTabSlice } from '../WidgetTabSlice';
import type { NewsQueryFn, NewsWidgetModel, NewsWidgetModelEventBus, NewsRow, NewsWidgetViewDetails } from './index';
import { Duration, ZoneId, Instant } from '@js-joda/core';
import type { LocalDate } from '@js-joda/core';
import { computed, effect, signal } from '@preact/signals-react';
import type { ReadonlySignal, Signal } from '@preact/signals-react';
import type { QueryObserverOptions } from '@tanstack/query-core';
import { Slugs } from '@thinkalpha/common/constants/slugs.js';
import { notNullOrUndefined } from '@thinkalpha/common/util/notNullOrUndefined.js';
import type { ConcreteIndicator } from '@thinkalpha/platform-ws-client/contracts/dictionary.js';
import { RowIdField } from '@thinkalpha/table-client';
import type { Logger } from '@thinkalpha/ui-ioc/core/types/Logger';
import { optional } from 'inversify';
import type { TableStatus } from 'src/components/table-view/model';
import type { IdeaDTO } from 'src/dtos/Idea';
import { SearchPlanDTO } from 'src/dtos/Idea/SearchPlanDTO';
import { ReactiveInjectable, reacts, inject, injectable } from 'src/features/ioc';
import { ScannerPlanDTO } from 'src/features/scanner/dtos/ScannerPlanDTO';
import type { ScreenerPlanDTO } from 'src/features/screener/dtos/ScreenerPlanDTO';
import type { InstrumentType } from 'src/features/symbol-picker/state';
import { ideaDTOToStage } from 'src/features/table/lib/ideaStages';
import type { WatchlistPlanDTO } from 'src/features/watchlist/dtos/WatchlistPlanDTO';
import { connectReducerDevtools } from 'src/lib/devtools';
import type { ReducerDevtoolsConnection } from 'src/lib/devtools';
import { getBulkNewsBySymbol } from 'src/lib/newQuant';
import { createTable } from 'src/lib/table';
import { FilterStage, OutputStage, SourceStage, UnionStage } from 'src/lib/tableStages';
import type { Stage } from 'src/lib/tableStages';
import type { NewsPersistedState, NewsWidgetAdapter } from 'src/models/NewsWidgetAdapter';
import { getIdeaDTOByIdQuery } from 'src/queries/ideas';
import type { WorkspaceService } from 'src/services/WorkspaceService';
import type { FetchableTableCreationResultWithColumns } from 'src/types';
import type { ReactBindings } from 'src/types/bindings';
import type { ConnectionStatusSignal } from 'src/types/EventBus';
import { EventBusBuilder, MergedEventBus } from 'src/types/EventBus/EventBus';
import type { SlowIdeaSource } from 'src/types/slowIdeaSource';
import { v4 } from 'uuid';

// Default duration a symbol remains in the news list after being removed from the table
const DEFAULT_SYMBOL_RETENTION = Duration.ofSeconds(0);

type NewsWidgetModelAction =
    | {
          type: 'INITIALIZE_SUCCESS';
          changeCloseSlug: Signal<ConcreteIndicator>;
          symbolSlug: Signal<ConcreteIndicator>;
          universeId: ReadonlySignal<string | null>;
          symbolFilter: string | null;
          symbolRetention: Duration;
      }
    | { type: 'UPDATE_FAST_IDEAS'; ideas: (IdeaDTO<ScannerPlanDTO> | IdeaDTO<SearchPlanDTO>)[] }
    | {
          type: 'UPDATE_SLOW_IDEAS';
          ideas: (IdeaDTO<ScreenerPlanDTO> | IdeaDTO<WatchlistPlanDTO> | { universeId: string })[];
      }
    | { type: 'UPDATE_SYMBOL_FILTER'; symbol: string | null }
    | { type: 'UPDATE_SYMBOL_RETENTION'; duration: Duration }
    | { type: 'SYMBOLS_SNAPSHOT_RECEIVED'; symbols: Iterable<string> }
    | { type: 'SYMBOLS_UPDATE_RECEIVED'; added: Iterable<string>; removed: Iterable<string> }
    | { type: 'CLEANUP_UNTRACKED_SYMBOLS' }
    | { type: 'RESET' };

type NewsWidgetModelState =
    | { type: 'initializing' }
    | {
          type: 'waiting-for-symbols';
          instanceId: string;
          changeCloseSlug: Signal<ConcreteIndicator>;
          symbolSlug: Signal<ConcreteIndicator>;
          universeId: ReadonlySignal<string | null>;
          fastIdeas: Signal<(IdeaDTO<ScannerPlanDTO> | IdeaDTO<SearchPlanDTO>)[]>;
          symbolFilter: Signal<string | null>;
          symbolRetention: Signal<Duration>;
      }
    | {
          type: 'ready';
          instanceId: string;
          changeCloseSlug: Signal<ConcreteIndicator>;
          symbolSlug: Signal<ConcreteIndicator>;
          universeId: ReadonlySignal<string | null>;
          fastIdeas: Signal<(IdeaDTO<ScannerPlanDTO> | IdeaDTO<SearchPlanDTO>)[]>;
          symbolFilter: Signal<string | null>;
          symbolRetention: Signal<Duration>;
          symbols: Signal<Set<string>>;
          untrackedSymbols: Signal<Map<string, Instant>>;
      };

export function newsWidgetModelReducer(
    state: NewsWidgetModelState,
    action: NewsWidgetModelAction,
): NewsWidgetModelState {
    switch (action.type) {
        case 'INITIALIZE_SUCCESS':
            return {
                type: 'waiting-for-symbols',
                instanceId: v4(),
                changeCloseSlug: action.changeCloseSlug,
                symbolSlug: action.symbolSlug,
                universeId: action.universeId,
                fastIdeas: signal<(IdeaDTO<ScannerPlanDTO> | IdeaDTO<SearchPlanDTO>)[]>([]),
                symbolFilter: signal<string | null>(action.symbolFilter),
                symbolRetention: signal<Duration>(action.symbolRetention),
            };
        case 'UPDATE_FAST_IDEAS':
            if (state.type === 'initializing') {
                return state;
            }

            // Check if ideas have changed
            const currentIdeas = state.fastIdeas.value;
            const newIdeaIds = action.ideas.map((idea) => idea.idea.id);
            const currentIdeaIds = currentIdeas.map((idea) => idea.idea.id);

            const ideasChanged =
                newIdeaIds.length !== currentIdeaIds.length || !newIdeaIds.every((id) => currentIdeaIds.includes(id));

            // Update the ideas value
            state.fastIdeas.value = action.ideas;

            // If ideas changed and we were in ready state, go back to waiting for symbols
            if (ideasChanged && state.type === 'ready') {
                return {
                    type: 'waiting-for-symbols',
                    instanceId: v4(),
                    changeCloseSlug: state.changeCloseSlug,
                    symbolSlug: state.symbolSlug,
                    universeId: state.universeId,
                    fastIdeas: state.fastIdeas,
                    symbolFilter: state.symbolFilter,
                    symbolRetention: state.symbolRetention,
                };
            }

            return state;
        case 'UPDATE_SLOW_IDEAS':
            if (state.type === 'initializing') {
                return state;
            }

            // Check for universe ID changes
            const slowIdeasWithUniverse = action.ideas.filter((idea) => 'universeId' in idea);
            const newUniverse = slowIdeasWithUniverse.length > 0 ? slowIdeasWithUniverse[0].universeId : null;

            // If we're in ready state and universe changed, go back to waiting for symbols
            if (state.type === 'ready' && state.universeId.value !== newUniverse) {
                return {
                    type: 'waiting-for-symbols',
                    instanceId: v4(),
                    changeCloseSlug: state.changeCloseSlug,
                    symbolSlug: state.symbolSlug,
                    universeId: state.universeId,
                    fastIdeas: state.fastIdeas,
                    symbolFilter: state.symbolFilter,
                    symbolRetention: state.symbolRetention,
                };
            }

            return state;
        case 'UPDATE_SYMBOL_FILTER':
            if (state.type !== 'initializing') {
                state.symbolFilter.value = action.symbol;
            }

            if (state.type === 'ready') {
                state.untrackedSymbols.value = new Map();
            }

            return state;
        case 'UPDATE_SYMBOL_RETENTION':
            if (state.type === 'initializing') {
                return state;
            }
            state.symbolRetention.value = action.duration;

            if (state.type !== 'ready') {
                return state;
            }

            if (action.duration.isZero()) {
                state.untrackedSymbols.value = new Map();
            }

            return state;
        case 'SYMBOLS_SNAPSHOT_RECEIVED':
            if (state.type === 'initializing') {
                return state;
            } else if (state.type === 'ready') {
                // We only update the signal value and return the existing state
                // This optimizes away a widget update, while the signal value
                // will still propagate to the components that read it
                const newSymbolsSet = new Set(action.symbols);

                // when we have a symbolFilter we won't have untracked symbols
                if (state.symbolFilter.value) {
                    state.symbols.value = newSymbolsSet;
                    state.untrackedSymbols.value = new Map();
                    return state;
                }

                const currentSymbolsSet = state.symbols.value;
                const untrackedMap = new Map(state.untrackedSymbols.value);
                const now = Instant.now();

                // Find symbols that are being removed (in current but not in new)
                // Add them to untracked but keep them in symbols until cleanup
                for (const symbol of currentSymbolsSet) {
                    if (!state.symbolRetention.value.isZero() && !newSymbolsSet.has(symbol)) {
                        untrackedMap.set(symbol, now);
                    }
                }

                // Add new symbols and remove them from untracked if they were there
                for (const symbol of newSymbolsSet) {
                    untrackedMap.delete(symbol);
                }

                // Combine new symbols with existing untracked symbols that haven't expired
                const combinedSymbols = new Set([...newSymbolsSet, ...untrackedMap.keys()]);

                state.symbols.value = combinedSymbols;
                state.untrackedSymbols.value = untrackedMap;
                return state;
            } else {
                return {
                    ...state,
                    type: 'ready',
                    symbols: signal<Set<string>>(new Set(action.symbols)),
                    untrackedSymbols: signal<Map<string, Instant>>(new Map()),
                };
            }
        case 'SYMBOLS_UPDATE_RECEIVED':
            if (state.type === 'initializing') {
                return state;
            } else if (state.type === 'ready') {
                // We only update the signal value and return the existing state
                // This optimizes away a widget update, while the signal value
                // will still propagate to the components that read it
                const currentSymbols = new Set(state.symbols.value);
                let untrackedMap = new Map(state.untrackedSymbols.value);
                const now = Instant.now();

                // Add new symbols and remove them from untracked if present
                for (const symbol of action.added) {
                    currentSymbols.add(symbol);
                    untrackedMap.delete(symbol);
                }

                if (!state.symbolRetention.value.isZero() && !state.symbolFilter.value) {
                    // Add removed symbols to untracked but keep them in symbols until cleanup
                    // if retention is not zero and we don't have a symbolFilter
                    for (const symbol of action.removed) {
                        untrackedMap.set(symbol, now);
                    }
                } else {
                    // if retention is zero or we have a symbolFilter, remove all symbols from untracked
                    for (const symbol of action.removed) {
                        currentSymbols.delete(symbol);
                    }
                    // reset untracked map to empty when retention is zero or when we have a symbolFilter
                    untrackedMap = new Map();
                }

                state.symbols.value = currentSymbols;
                state.untrackedSymbols.value = untrackedMap;
                return state;
            } else {
                return {
                    ...state,
                    type: 'ready',
                    symbols: signal<Set<string>>(new Set(action.added)),
                    untrackedSymbols: signal<Map<string, Instant>>(new Map()),
                };
            }
        case 'CLEANUP_UNTRACKED_SYMBOLS': {
            if (state.type !== 'ready') {
                return state;
            }

            const cutoffTime = Instant.now().minus(state.symbolRetention.value);
            const untrackedMap = new Map(state.untrackedSymbols.value);
            const currentSymbols = new Set(state.symbols.value);
            let hasChanges = false;

            for (const [symbol, removalTime] of untrackedMap) {
                if (removalTime.isBefore(cutoffTime)) {
                    untrackedMap.delete(symbol);
                    currentSymbols.delete(symbol);
                    hasChanges = true;
                }
            }

            if (hasChanges) {
                state.untrackedSymbols.value = untrackedMap;
                state.symbols.value = currentSymbols;
            }
            return state;
        }
        case 'RESET':
            return { type: 'initializing' };
        default:
            return state;
    }
}

@injectable()
export class NewsWidgetModelImpl extends ReactiveInjectable implements NewsWidgetModel {
    #adapter: NewsWidgetAdapter;
    #queryClient: ReactBindings['QueryClient'];
    #formulaService: ReactBindings['FormulaService'];
    #table: ReactBindings['TableModel'];
    #defaultUniverseServiceProvider: ReactBindings['DefaultUniverseServiceProvider'];
    #slugMapService: ReactBindings['SlugMapService'];
    #slugMapServiceProvider: ReactBindings['SlugMapServiceProvider'];
    #channelSlice: ChannelSlice;
    #widgetTabSlice: WidgetTabSlice;

    #allowedInstrumentTypes: ReadonlySignal<InstrumentType[]> = signal(['equity']);

    // New header system signals
    #connectionStatus: Signal<
        ConnectionStatusSignal['widget::connectionStatus'] extends ReadonlySignal<infer T> ? T : never
    > = signal({
        status: 'uninitialized' as TableStatus,
        progress: undefined,
        failureCount: 0,
    });

    headerElements!: WidgetHeaderElement[];

    eventBus!: NewsWidgetModel['eventBus'];

    constructor(
        @inject('NewsWidgetAdapter') adapter: NewsWidgetAdapter,
        @inject('Store') store: ReactBindings['Store'],
        @inject('Logger') logger: Logger,
        @inject('DefaultUniverseServiceProvider')
        defaultUniverseServiceProvider: ReactBindings['DefaultUniverseServiceProvider'],
        @inject('SlugMapServiceProvider') slugMapServiceProvider: ReactBindings['SlugMapServiceProvider'],
        @inject('SlugMapService') slugMapService: ReactBindings['SlugMapService'],
        @inject('TableModel') table: ReactBindings['TableModel'],
        @inject('QueryClient') queryClient: ReactBindings['QueryClient'],
        @inject('FormulaService') formulaService: ReactBindings['FormulaService'],
        @inject('ChannelSlice') channelSlice: ChannelSlice,
        @inject('WidgetTabSlice') widgetTabSlice: WidgetTabSlice,
        @inject('WorkspaceService') @optional() workspaceService?: WorkspaceService,
        @inject('TabId') @optional() tabId?: string,
    ) {
        // eslint-disable-next-line prefer-rest-params -- forwarding constructor args
        super(...arguments);

        this.#adapter = adapter;
        this.#queryClient = queryClient;
        this.#formulaService = formulaService;
        this.#table = table;
        this.#defaultUniverseServiceProvider = defaultUniverseServiceProvider;
        this.#slugMapService = slugMapService;
        this.#slugMapServiceProvider = slugMapServiceProvider;
        this.#channelSlice = channelSlice;
        this.#widgetTabSlice = widgetTabSlice;

        this.#state = { type: 'initializing' };

        this.#logger = logger.getSubLogger({ name: 'NewsWidgetModel' });

        if (tabId && workspaceService) {
            workspaceService.registerWidget(tabId, this);
            this.disposableStack.defer(() => workspaceService.deregisterWidget(tabId));
        }
    }

    get fastIdeas(): ReadonlySignal<(IdeaDTO<ScannerPlanDTO> | IdeaDTO<SearchPlanDTO>)[]> | null {
        if (this.#state.type !== 'initializing') {
            return this.#state.fastIdeas;
        }
        return null;
    }

    async init(tabId?: string) {
        const baseElements = tabId
            ? COMMON_HEADER_ELEMENTS
            : STANDALONE_COMMON_HEADER_ELEMENTS.filter((el) => el.type !== 'channelPicker');
        this.headerElements = [
            ...baseElements,
            // Left side (< 50)
            { type: 'symbolPicker', position: 25, priority: 80, placeholder: 'symbol' },
            { type: 'symbolRetention', position: 28, priority: 50 },
            { type: 'helpDocument', documentId: 'news-overview', position: 30, priority: 40 },
            // Right side (> 50, spacer is at 50)
            {
                type: 'ideasMenu',
                key: 'slow',
                position: 54,
                priority: 60,

                selectType: 'single',
                ideaType: 'slow',
                ideaPlanTypes: ['universe', 'screener', 'watchlist'],
                showSelected: false,
                allowDelete: false,
            },
            {
                type: 'ideasMenu',
                key: 'fast',
                position: 56,
                priority: 60,

                selectType: 'multi',
                ideaType: 'fast',
                ideaPlanTypes: ['scanner', 'if-then'],
                showSelected: true,
                allowDelete: false,
            },
            { type: 'connectionStatus', position: 995, priority: 995 },
        ];

        this.#devtools = connectReducerDevtools({ name: `News [${tabId ?? 'standalone'}]` });
        if (this.#devtools) {
            this.disposableStack.use(this.#devtools);
            this.#devtools.init(this.#state);
        }

        if (tabId) {
            // Initialize slices for the new widget header system (workspace only)
            this.#channelSlice.init(tabId);
            this.#widgetTabSlice.init(tabId);
            this.disposableStack.use(this.#channelSlice);
            this.disposableStack.use(this.#widgetTabSlice);
        }

        // Build the EventBus (must be after slice init so slices provide signals in workspace mode)
        this.eventBus = this.#buildEventBus(tabId);

        const adapterState = this.#adapter.getState();

        // The universeId field stores either a universe ID or a screener/watchlist idea ID.
        // On init, probe the stored value: if it resolves as an idea DTO it's a screener/watchlist;
        // otherwise treat it as a universe ID.
        const storedId = adapterState.universeId;
        if (storedId) {
            try {
                const dto = await this.#queryClient.fetchQuery(
                    getIdeaDTOByIdQuery(this.#queryClient, this.#formulaService, this.#slugMapService, storedId),
                );
                if (TRACE) {
                    this.#logger.trace('Initializing with slow idea from adapter', { ideaId: storedId });
                }
                this.#slowIdeaSourceSignal.value = {
                    type: 'idea',
                    id: storedId,
                    dto: dto as IdeaDTO<ScreenerPlanDTO | WatchlistPlanDTO>,
                };
            } catch {
                if (TRACE) {
                    this.#logger.trace('Initializing with universe from adapter', { universeId: storedId });
                }
                this.#slowIdeaSourceSignal.value = { type: 'universe', id: storedId };
            }
        } else {
            // No stored ID — initialize with the default universe
            const defaultUniverseService = await this.#defaultUniverseServiceProvider();
            const defaultUniverseId = defaultUniverseService.universe.value.id;
            if (TRACE) {
                this.#logger.trace('Initializing with default universe', { defaultUniverseId });
            }
            this.#adapter.persist({ universeId: defaultUniverseId });
            this.#slowIdeaSourceSignal.value = { type: 'universe', id: defaultUniverseId };
        }
        this.disposableStack.defer(
            this.#adapter.subscribe((nextAdapterState: NewsPersistedState) => {
                const newStoredId = nextAdapterState.universeId;
                const currentId = this.#slowIdeaSourceSignal.value?.id ?? null;

                if (newStoredId !== currentId) {
                    // Treat adapter-driven changes as universe selections (ideas are set in-memory first)
                    this.#slowIdeaSourceSignal.value = newStoredId ? { type: 'universe', id: newStoredId } : null;
                    if (TRACE) {
                        this.#logger.trace({ message: 'User selected new universe', universeId: newStoredId });
                    }
                    if (this.#state.type !== 'initializing') {
                        this.#updateState({
                            type: 'UPDATE_SLOW_IDEAS',
                            ideas: newStoredId ? [{ universeId: newStoredId }] : [],
                        });
                    }
                }

                // Update symbolFilter in state from adapter
                if (this.#state.type !== 'initializing') {
                    // Only update if the values are actually different, treating null and empty string as equivalent
                    const currentValue = this.#state.symbolFilter.value;
                    const newValue = nextAdapterState.symbolFilter ?? null;

                    // Skip update if they're identical or if one is null and the other is empty string
                    if (
                        currentValue !== newValue &&
                        !((currentValue === null && newValue === '') || (currentValue === '' && newValue === null))
                    ) {
                        this.#updateState({ type: 'UPDATE_SYMBOL_FILTER', symbol: nextAdapterState.symbolFilter });
                    }
                }

                // Update symbolRetention in state from adapter
                if (this.#state.type !== 'initializing') {
                    const adapterRetention = nextAdapterState.symbolRetention;
                    const reduxDuration = adapterRetention
                        ? Duration.parse(adapterRetention)
                        : DEFAULT_SYMBOL_RETENTION;
                    const currentDuration = this.#state.symbolRetention.value;
                    if (!reduxDuration.equals(currentDuration)) {
                        this.#updateState({ type: 'UPDATE_SYMBOL_RETENTION', duration: reduxDuration });
                    }
                }

                void this.#syncFastIdeasFromAdapter(nextAdapterState.fastIdeasIds).catch((err) => {
                    this.#logger.error('Failed to sync fast ideas from adapter', {
                        error: err instanceof Error ? err.message : err,
                    });
                });
            }),
        );

        const slugMapService = await this.#slugMapServiceProvider();

        const symbolSlug = computed(() => slugMapService.slugMap.value[Slugs.symbol]);
        const changeCloseSlug = computed(() => slugMapService.slugMap.value[Slugs.symbolChangeClose]);

        // Hydrate symbolRetention from adapter, or use default if not set
        const adapterSymbolRetention = adapterState.symbolRetention;
        const initialSymbolRetention = adapterSymbolRetention
            ? Duration.parse(adapterSymbolRetention)
            : DEFAULT_SYMBOL_RETENTION;

        // Hydrate symbolFilter from adapter
        const initialSymbolFilter = adapterState.symbolFilter ?? null;

        this.#updateState({
            type: 'INITIALIZE_SUCCESS',
            changeCloseSlug,
            symbolSlug,
            universeId: this.#universeId,
            symbolFilter: initialSymbolFilter,
            symbolRetention: initialSymbolRetention,
        });

        await this.#syncFastIdeasFromAdapter(adapterState.fastIdeasIds);

        // Initialize query options signal once in constructor
        const queryOptions: Signal<QueryObserverOptions<FetchableTableCreationResultWithColumns>> = computed(() => {
            return {
                queryKey: [
                    'news-widget',
                    'table',
                    this.symbolFilter?.value ?? null,
                    this.#slowIdeaSourceSignal.value?.id ?? null,
                    (this.#state.type !== 'initializing' ? this.#state.fastIdeas.value : []).map(
                        (idea) => idea.idea.id,
                    ),
                ],
                queryFn: async () => {
                    const symbolFilterValue = this.symbolFilter?.value ?? null;
                    let stage: Stage;
                    const source = this.#slowIdeaSourceSignal.value;
                    if (symbolFilterValue) {
                        stage = SourceStage.allForKey('ticker');
                    } else if (source?.type === 'idea') {
                        const allStage = SourceStage.allForKey('ticker');
                        stage = ideaDTOToStage(allStage, source.dto);
                    } else if (!source) {
                        stage = SourceStage.allForKey('ticker');
                    } else {
                        stage = SourceStage.fromTickerUniverseId(source.id);
                    }

                    if (symbolFilterValue) {
                        stage = FilterStage.fromInputStage(stage, {
                            type: 'filter',
                            formula: `symbol == "${symbolFilterValue}"`,
                            imports: [{ ...symbolSlug.value, alias: 'symbol' }],
                        });
                    } else if (this.#state.type !== 'initializing') {
                        // Handle fast ideas
                        const fastIdeasValue = this.#state.fastIdeas.value;
                        if (fastIdeasValue.length > 1) {
                            const stages = fastIdeasValue.map((idea) => ideaDTOToStage(stage, idea));

                            if (stages.length > 0) {
                                stage = UnionStage.from(stages);
                            }
                        } else if (fastIdeasValue.length === 1) {
                            const idea = fastIdeasValue[0];
                            stage = ideaDTOToStage(stage, idea);
                        }
                    }

                    const formula = 'changeClose > 0 ? 1 : changeClose < 0 ? -1 : 0';
                    const imports = [{ ...changeCloseSlug.value, alias: 'changeClose' }];

                    stage = OutputStage.fromInputStage(stage, {}, [
                        { id: 'symbol', type: 'indicator', indicator: symbolSlug.value },
                        { id: 'changeDirection', type: 'formula', formula, imports },
                    ]);

                    return {
                        ...(await createTable({
                            leafStage: stage.build(),
                        })),
                        fetchMode: 'snapshot',
                    };
                },
                enabled: true,
                staleTime: 0,
                gcTime: 0,
                refetchOnWindowFocus: false,
            };
        });

        this.#table.init({
            initBounds: { firstRow: 0, windowSize: 100000 },
            initThrottle: 1000,
            queryOptions,
            devtoolsName: 'News/table',
        });

        // Use table updates$ directly without snapshots
        // Subscribe to updates$ for real-time changes
        this.disposableStack.use(
            this.#table.updatesWithReset$.subscribe((updates) => {
                if (!updates) return;

                if (updates === NEW_TABLE_RESET) {
                    this.#updateState({
                        type: 'SYMBOLS_SNAPSHOT_RECEIVED',
                        symbols: [],
                    });
                    this.#symbolChangeDirectionsMap.value = new Map();
                    return;
                }

                if (updates.type === 'snapshot') {
                    this.#rowIdToSymbolMap.clear();
                    const newSymbols = new Set<string>();
                    // Create a new Map to avoid modifying the one in the signal
                    const changeDirections = new Map<string, number>();

                    for (const update of updates) {
                        this.#rowIdToSymbolMap.set(update[RowIdField]!, update.symbol);
                        newSymbols.add(update.symbol);

                        // Get change direction directly from the changeDirection field
                        const changeDirection = Number(update['changeDirection']);
                        if (!isNaN(changeDirection)) {
                            changeDirections.set(update.symbol, changeDirection);
                        }
                    }

                    // Update the change directions signal
                    this.#symbolChangeDirectionsMap.value = changeDirections;

                    this.#updateState({
                        type: 'SYMBOLS_SNAPSHOT_RECEIVED',
                        symbols: newSymbols,
                    });
                    return;
                } else if (updates.type === 'diff') {
                    const removedSymbols = new Set<string>();
                    const addedSymbols = new Set<string>();
                    // Create a new Map from the current change directions
                    const changeDirections = new Map(this.#symbolChangeDirectionsMap.value);

                    for (const removed of updates.deletedRowIds) {
                        const deadSymbol = this.#rowIdToSymbolMap.get(removed);
                        if (!deadSymbol) continue;
                        this.#rowIdToSymbolMap.delete(removed);
                        // Remove the symbol from change directions map
                        changeDirections.delete(deadSymbol);
                        removedSymbols.add(deadSymbol);
                    }

                    for (const update of updates) {
                        if (TRACE && removedSymbols.has(update.symbol)) {
                            this.#logger.trace('Found update for removed symbol', {
                                symbol: update.symbol,
                                rowId: update[RowIdField],
                            });
                        }
                        const symbol = update['symbol'] as string;
                        if (symbol) {
                            // Only consider it an addition if it's not a symbol we already know about
                            const currentlyTracked =
                                this.#state.type === 'ready' && this.#state.symbols.value.has(symbol);

                            // Get change direction directly from the changeDirection field
                            const changeDirection = Number(update['changeDirection']);
                            if (!isNaN(changeDirection)) {
                                changeDirections.set(symbol, changeDirection);
                            }

                            if (!currentlyTracked) {
                                this.#rowIdToSymbolMap.set(update[RowIdField]!, update.symbol);
                                addedSymbols.add(symbol);
                            }
                        }
                    }

                    // Update the change directions signal
                    this.#symbolChangeDirectionsMap.value = changeDirections;

                    if (addedSymbols.size > 0 || removedSymbols.size > 0) {
                        if (TRACE) {
                            for (const symbol of addedSymbols) {
                                this.#logger.trace(`New symbol added to table: ${symbol}`);
                            }
                            for (const symbol of removedSymbols) {
                                this.#logger.trace(`Symbol removed from table: ${symbol}`);
                            }
                        }
                        this.#updateState({
                            type: 'SYMBOLS_UPDATE_RECEIVED',
                            added: addedSymbols,
                            removed: removedSymbols,
                        });
                    }
                } else if (updates.type === 'quote') {
                    // Create a new Map from the current change directions
                    const changeDirections = new Map(this.#symbolChangeDirectionsMap.value);

                    // Handle quote updates
                    for (const update of updates) {
                        const symbol = update.symbol;
                        if (symbol) {
                            // Get change direction directly from the changeDirection field
                            const changeDirection = Number(update['changeDirection']);
                            if (!isNaN(changeDirection)) {
                                changeDirections.set(symbol, changeDirection);
                            }
                        }
                    }

                    // Update the change directions signal
                    this.#symbolChangeDirectionsMap.value = changeDirections;
                }
            }),
        );

        this.disposableStack.use(
            this.#table.count$.subscribe((count) => {
                if (count === 0 && this.#state.type === 'waiting-for-symbols') {
                    // If we have no symbols and were waiting for them, reset the state
                    if (TRACE) {
                        this.#logger.trace('No symbols found, resetting state');
                    }
                    this.#updateState({ type: 'SYMBOLS_SNAPSHOT_RECEIVED', symbols: [] });
                }
            }),
        );

        // Set up periodic cleanup of untracked symbols
        const cleanupInterval = setInterval(() => {
            this.#updateState({ type: 'CLEANUP_UNTRACKED_SYMBOLS' });
        }, 5000); // 5 seconds
        this.disposableStack.defer(() => clearInterval(cleanupInterval));

        // Sync connection status for new header system
        this.disposableStack.defer(
            effect(() => {
                const status = this.#table.connectionStatus.value;
                const progress = this.#table.progress.value;
                const failureCount = this.#table.failureCount.value;
                this.#connectionStatus.value = {
                    status,
                    progress,
                    failureCount,
                };
            }),
        );
    }

    #logger: Logger;

    /**
     * Builds the EventBus for the new widget header system.
     * Combines widget-specific signals/events with slice-provided ones.
     */
    #buildEventBus(tabId?: string): NewsWidgetModel['eventBus'] {
        let merged = MergedEventBus.from(
            EventBusBuilder.create()
                // News-specific signals
                .addSignal('widget::instrument', () =>
                    computed(() => {
                        const state = this.#stateSignal.value;
                        if (state.type === 'initializing') return '';
                        return state.symbolFilter.value ?? '';
                    }),
                )
                .addSignal('widget::allowedInstrumentTypes', () => this.#allowedInstrumentTypes)
                .addSignal('widget::symbolRetention', () =>
                    computed(() => {
                        const state = this.#stateSignal.value;
                        if (state.type === 'initializing') return Duration.ofSeconds(0);
                        return state.symbolRetention.value;
                    }),
                )
                .addSignal('widget::connectionStatus', () => this.#connectionStatus)
                .addSignal('widget::slowIdea', () =>
                    computed(() => {
                        return this.#slowIdeaDTO.value;
                    }),
                )
                .addSignal('widget::selectedFastIdeas', () =>
                    computed(() => {
                        const state = this.#stateSignal.value;
                        if (state.type === 'initializing') return [];
                        return state.fastIdeas.value.flatMap((idea) => {
                            const id = idea.idea.id;
                            return id !== undefined ? [{ type: 'idea' as const, id }] : [];
                        });
                    }),
                )
                .addSignal('widget::selectedSlowIdeas', () =>
                    computed(() => {
                        const source = this.#slowIdeaSourceSignal.value;
                        if (!source) return [];
                        return [{ type: source.type, id: source.id }];
                    }),
                )
                // News-specific events
                .addEvent('widget::instrumentChange', (payload) => {
                    this.onChangeSymbolFilter(payload.instrument);
                })
                .addEvent('widget::updateSymbolRetention', (payload) => {
                    this.onChangeSymbolRetention(Duration.parse(payload.duration));
                })
                .addEvent('widget::onIdeasSelect', (payload) => {
                    // This event is only emitted for fast ideas by the IdeasMenu component
                    // (slow ideas use widget::selectSlowIdea instead)
                    const fastIdeas = payload.ideas.filter((idea) => !('universeId' in idea)) as (
                        | IdeaDTO<ScannerPlanDTO>
                        | IdeaDTO<SearchPlanDTO>
                    )[];
                    // Always forward to handler, even empty array (to clear selection)
                    this.onSelectFastIdeas(fastIdeas);
                })
                .addEvent('widget::onIdeaDelete', () => {
                    // News widget doesn't support idea deletion
                })
                .addEvent('widget::selectSlowIdea', (payload) => {
                    // Type assertion needed because event uses generic IdeaDTO while method expects specific plan types
                    this.onSelectSlowIdeas(payload.ideas as Parameters<typeof this.onSelectSlowIdeas>[0]);
                })
                .addEvent('widget::connectionStatusRefetch', () => {
                    this.refetch();
                })
                .build(),
        );

        if (tabId) {
            // Workspace mode: merge slice event buses (they provide widget::name, channel signals, etc.)
            merged = merged.merge(this.#channelSlice.eventBus).merge(this.#widgetTabSlice.eventBus);
        } else {
            // Standalone mode: provide a static name signal since slices aren't initialized
            merged = merged.merge(
                EventBusBuilder.create()
                    .addSignal('widget::name', () => signal('News'))
                    .build(),
            );
        }

        return merged as NewsWidgetModelEventBus;
    }

    #rowIdToSymbolMap = new Map<number, string>();
    #symbolChangeDirectionsMap = signal<Map<string, number>>(new Map());

    get symbolChangeDirections(): ReadonlySignal<ReadonlyMap<string, number>> {
        return this.#symbolChangeDirectionsMap;
    }

    private isUniverseIdFormat(idea: any): idea is { universeId: string } {
        return idea && typeof idea === 'object' && 'universeId' in idea && typeof idea.universeId === 'string';
    }

    private isTypeIdFormat(idea: any): idea is { type: string; id: string } {
        return (
            idea &&
            typeof idea === 'object' &&
            'type' in idea &&
            idea.type === 'universe' &&
            'id' in idea &&
            typeof idea.id === 'string'
        );
    }

    onSelectSlowIdeas(
        ideas: (
            | IdeaDTO<ScreenerPlanDTO>
            | IdeaDTO<WatchlistPlanDTO>
            | { universeId: string }
            | { type: string; id: string }
        )[],
    ): void {
        if (TRACE) {
            this.#logger.trace('onSelectSlowIdeas received ideas', {
                ideas: JSON.stringify(ideas),
                currentSource: this.#slowIdeaSourceSignal.value,
            });
        }

        const firstIdea = ideas[0];

        if (!firstIdea) {
            // Clear selection
            this.#slowIdeaSourceSignal.value = null;
            this.#adapter.persist({ universeId: null });
            return;
        }

        // Check for IdeaDTO (screener or watchlist) — has an 'idea' property
        if ('idea' in firstIdea) {
            const ideaDTO = firstIdea as IdeaDTO<ScreenerPlanDTO | WatchlistPlanDTO>;
            const ideaId = ideaDTO.idea.id ?? null;
            if (TRACE) {
                this.#logger.trace('Selected screener/watchlist slow idea', { ideaId });
            }
            // Set in-memory signal first so the adapter subscription can detect it's an idea
            this.#slowIdeaSourceSignal.value = ideaId ? { type: 'idea', id: ideaId, dto: ideaDTO } : null;
            // Store the idea ID in the universeId field (single-field paradigm)
            if (ideaId !== this.#adapter.getState().universeId) {
                this.#adapter.persist({ universeId: ideaId });
            }
            return;
        }

        let selectedUniverse: string | null = null;

        const ideaWithUniverseId = ideas.find((idea) => this.isUniverseIdFormat(idea));
        if (ideaWithUniverseId) {
            selectedUniverse = ideaWithUniverseId.universeId;
        } else {
            const ideaWithTypeUniverse = ideas.find((idea) => this.isTypeIdFormat(idea));
            if (ideaWithTypeUniverse) {
                selectedUniverse = ideaWithTypeUniverse.id;
            }
        }

        if (TRACE) {
            this.#logger.trace('Selected universe', {
                selectedUniverse,
                currentSource: this.#slowIdeaSourceSignal.value,
            });
        }

        const currentId = this.#slowIdeaSourceSignal.value?.id ?? null;
        if (currentId !== selectedUniverse) {
            this.#slowIdeaSourceSignal.value = selectedUniverse ? { type: 'universe', id: selectedUniverse } : null;
            this.#adapter.persist({ universeId: selectedUniverse });
        }
    }

    onSelectFastIdeas(ideas: (IdeaDTO<ScannerPlanDTO> | IdeaDTO<SearchPlanDTO>)[]): void {
        this.#updateState({ type: 'UPDATE_FAST_IDEAS', ideas });
        const ideaIds = ideas.map((idea) => idea.idea.id).filter((id): id is string => id !== undefined);
        this.#adapter.persist({ fastIdeasIds: ideaIds });
    }

    async #syncFastIdeasFromAdapter(fastIdeaIds: string[]): Promise<void> {
        if (this.#state.type === 'initializing') {
            return;
        }

        const currentIdeaIds = this.#state.fastIdeas.value
            .map((idea) => idea.idea.id)
            .filter((id): id is string => id !== undefined);

        if (
            fastIdeaIds.length === currentIdeaIds.length &&
            fastIdeaIds.every((ideaId, index) => ideaId === currentIdeaIds[index])
        ) {
            return;
        }

        const ideaResults = await Promise.all(
            fastIdeaIds.map((id) =>
                this.#queryClient
                    .fetchQuery(getIdeaDTOByIdQuery(this.#queryClient, this.#formulaService, this.#slugMapService, id))
                    .catch(() => null),
            ),
        );

        const validIdeas = ideaResults
            .filter(notNullOrUndefined)
            .filter(
                (idea): idea is IdeaDTO<ScannerPlanDTO> | IdeaDTO<SearchPlanDTO> =>
                    idea.plan instanceof ScannerPlanDTO || idea.plan instanceof SearchPlanDTO,
            );

        this.#updateState({ type: 'UPDATE_FAST_IDEAS', ideas: validIdeas });
    }

    onSelectSymbol(symbol: string) {
        this.#adapter.onSelectSymbol(symbol);
    }

    openChartForSymbol(symbol: string) {
        this.#adapter.openChartForSymbol(symbol);
    }

    openLensForSymbol(symbol: string) {
        this.#adapter.openLensForSymbol(symbol);
    }

    #resetState() {
        if (this.#state.type === 'initializing') {
            return;
        }

        this.#adapter.persist({ symbolFilter: null });

        this.#updateState({ type: 'RESET' });
    }

    #state: NewsWidgetModelState;
    #stateSignal = signal<NewsWidgetModelState>({ type: 'initializing' });
    #devtools: ReducerDevtoolsConnection<NewsWidgetModelState, NewsWidgetModelAction> | undefined;

    // Keeping this @reacts setter for backward compatibility, transition to using #stateSignal directly
    @reacts private set state(newState: NewsWidgetModelState) {
        this.#state = newState;
        this.#stateSignal.value = newState;
    }

    #updateState(action: NewsWidgetModelAction) {
        const prevState = this.#state;
        if (TRACE) {
            this.#logger.trace('Dispatching action', { type: action.type });
        }
        const newState = newsWidgetModelReducer(this.#state, action);

        if (prevState !== newState) {
            if (TRACE) {
                this.#logger.trace('State updated', { action, prevState, state: this.#state });
            }

            this.state = newState;
            this.#devtools?.send(action, this.#state);

            // Keep slowIdeaSourceSignal in sync with adapter — but only when no slow idea is active,
            // since the universeId field also stores screener/watchlist idea IDs.
            if (
                this.#slowIdeaSourceSignal.value?.type !== 'idea' &&
                this.#adapter.getState().universeId !== (this.#slowIdeaSourceSignal.value?.id ?? null)
            ) {
                const adapterUniverseId = this.#adapter.getState().universeId;
                this.#slowIdeaSourceSignal.value = adapterUniverseId
                    ? { type: 'universe', id: adapterUniverseId }
                    : null;
            }

            if (newState.type === 'initializing') {
                this.#viewDetailsSignal.value = { type: 'loading' };
            } else if (newState.type === 'waiting-for-symbols') {
                this.#viewDetailsSignal.value = {
                    type: 'waiting-for-symbols',
                };
            } else {
                this.#viewDetailsSignal.value = {
                    type: 'ready',
                    instanceId: newState.instanceId,
                    symbols: newState.symbols,
                    retiringSymbols: newState.untrackedSymbols,
                    symbolFilter: newState.symbolFilter,
                    fastIdeas: newState.fastIdeas,
                };
            }
        }
    }

    get symbolFilter(): ReadonlySignal<string | null> | null {
        if (this.#state.type !== 'initializing') {
            return this.#state.symbolFilter;
        }
        return null;
    }

    get symbolRetention(): ReadonlySignal<Duration> | null {
        if (this.#state.type !== 'initializing') {
            return this.#state.symbolRetention;
        }
        return null;
    }

    // Method to change the symbol filter
    onChangeSymbolFilter(value: string) {
        // Don't dispatch if the value is already the same to prevent loops
        // This is especially important for empty string vs null comparisons
        const currentValue = this.#state.type !== 'initializing' ? this.#state.symbolFilter.value : null;
        if ((value === '' && currentValue === null) || value === currentValue) {
            return;
        }
        this.#adapter.persist({ symbolFilter: value === '' ? null : value });
    }

    // Method to change the symbol retention duration
    onChangeSymbolRetention(duration: Duration) {
        this.#adapter.persist({ symbolRetention: duration.toString() });
    }

    get symbols(): ReadonlySignal<ReadonlySet<string>> | null {
        if (this.#state.type === 'ready') {
            return this.#state.symbols as ReadonlySignal<ReadonlySet<string>>;
        }
        return null;
    }

    // Expose news from chronological query as a signal
    #news = signal<NewsRow[]>([]);

    get news() {
        return this.#news as ReadonlySignal<NewsRow[]>;
    }

    // Define the query function directly without a signal
    #chronologicalQueryFn: NewsQueryFn = async (
        symbols: string[] | null,
        prevRow: NewsRow | null,
        pinnedDate: LocalDate | null,
        _abortSignal: AbortSignal,
    ): Promise<Iterable<NewsRow>> => {
        if (TRACE) {
            this.#logger.trace('Query function called', { symbols, prevRow });
        }
        if (!symbols || symbols.length === 0) return [];

        try {
            // Convert pinnedDate (LocalDate) to Instant if provided
            let endTimestamp: Instant | undefined = undefined;

            if (pinnedDate) {
                // Convert LocalDate to Instant: end of day in system timezone
                endTimestamp = pinnedDate.atTime(23, 59, 59).atZone(ZoneId.SYSTEM).toInstant();

                if (TRACE) {
                    this.#logger.trace('Converting pinnedDate to Instant for news query', {
                        pinnedDate: pinnedDate.toString(),
                        endTimestamp: endTimestamp.toString(),
                    });
                }
            } else if (prevRow) {
                endTimestamp = prevRow.publishedAt;
            }

            // Use the existing getBulkNewsBySymbol API to fetch news data
            const news = await getBulkNewsBySymbol({
                symbols: symbols,
                end: endTimestamp,
                maxResults: 50,
            });

            // Transform the news into the NewsRow format required by ChronologicalQueryManager
            return news.map((newsItem) => {
                return {
                    ...newsItem,
                    timestamp: newsItem.publishedAt, // Use publishedAt directly as the timestamp for chronological ordering
                } as NewsRow;
            });
        } catch (error) {
            this.#logger.error('Error fetching news', { error });
            return [];
        }
    };

    get newsQueryFn(): NewsQueryFn {
        return this.#chronologicalQueryFn;
    }

    get slowIdeaSource(): ReadonlySignal<SlowIdeaSource | null> {
        return this.#slowIdeaSourceSignal;
    }

    #slowIdeaSourceSignal = signal<SlowIdeaSource | null>(null);
    // Convenience computeds for internal consumption
    #universeId = computed(() => {
        const s = this.#slowIdeaSourceSignal.value;
        return s?.type === 'universe' ? s.id : null;
    });
    #slowIdeaDTO = computed(() => {
        const s = this.#slowIdeaSourceSignal.value;
        return s?.type === 'idea' ? s.dto : null;
    });

    #viewDetailsSignal = signal<NewsWidgetViewDetails>({ type: 'loading' });

    get viewDetails(): ReadonlySignal<NewsWidgetViewDetails> {
        return this.#viewDetailsSignal;
    }

    get connectionStatus(): ReadonlySignal<TableStatus> {
        return this.#table.connectionStatus;
    }

    get progress(): ReadonlySignal<number | undefined> {
        return this.#table.progress;
    }

    refetch(): void {
        this.#table.refetch();
    }
}
